import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface Session {
  duration: number;
  goal: string;
  mood: string;
  notes: string;
  tags: string[];
}

type TimerMode = 'focus' | 'shortBreak' | 'longBreak';

export default function FocusAndLearn() {
  const [timeLeft, setTimeLeft] = useState<number>(25 * 60);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [selectedDuration, setSelectedDuration] = useState<number>(25);
  const [goal, setGoal] = useState<string>('');
  const [mood, setMood] = useState<string>('😊');
  const [isAmbientSoundOn, setIsAmbientSoundOn] = useState<boolean>(false);
  const [showReflection, setShowReflection] = useState<boolean>(false);
  const [sessionNotes, setSessionNotes] = useState<string>('');
  const [sessionTags, setSessionTags] = useState<string[]>([]);
  const [distractions, setDistractions] = useState<string[]>([]);
  const [completedSessions, setCompletedSessions] = useState<number>(0);
  const [timerMode, setTimerMode] = useState<TimerMode>('focus');
  const [selectedShortBreakDuration, setSelectedShortBreakDuration] = useState<number>(4);
  const [selectedLongBreakDuration, setSelectedLongBreakDuration] = useState<number>(15);

  const focusDurations = [20, 25, 30];
  const shortBreakDurations = [4, 5, 6];
  const longBreakDurations = [15, 20, 25];
  const moods = ['😊', '😐', '😢', '😴', '🤔'];

  // Ambient sound state
  const [ambientSound, setAmbientSound] = useState<HTMLAudioElement | null>(null);

  // Play/pause rain sound based on session and toggle
  useEffect(() => {
    if (!ambientSound) return;
    if (isActive && isAmbientSoundOn) {
      ambientSound.play();
    } else {
      ambientSound.pause();
      ambientSound.currentTime = 0;
    }
  }, [isActive, isAmbientSoundOn, ambientSound]);

  useEffect(() => {
    const audio = new Audio('/sounds/rain.mp3');
    audio.loop = true;
    setAmbientSound(audio);
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setIsActive(false);
      if (timerMode === 'focus') {
        setCompletedSessions(prev => prev + 1);
        if (completedSessions + 1 >= 4) {
          setTimerMode('longBreak');
          setTimeLeft(selectedLongBreakDuration * 60);
        } else {
          setTimerMode('shortBreak');
          setTimeLeft(selectedShortBreakDuration * 60);
        }
      } else if (timerMode === 'shortBreak') {
        setTimerMode('focus');
        setTimeLeft(selectedDuration * 60);
      } else if (timerMode === 'longBreak') {
        setCompletedSessions(0);
        setTimerMode('focus');
        setTimeLeft(selectedDuration * 60);
      }
    }
    return () => clearInterval(interval);
  }, [isActive, timeLeft, timerMode, completedSessions, selectedDuration, selectedShortBreakDuration, selectedLongBreakDuration]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStart = () => {
    if (timerMode === 'focus') {
      setTimeLeft(selectedDuration * 60);
    } else if (timerMode === 'shortBreak') {
      setTimeLeft(selectedShortBreakDuration * 60);
    } else if (timerMode === 'longBreak') {
      setTimeLeft(selectedLongBreakDuration * 60);
    }
    setIsActive(true);
    // Rain sound will play via useEffect if toggle is ON
  };

  const handlePause = () => {
    setIsActive(false);
    if (ambientSound) {
      ambientSound.pause();
      ambientSound.currentTime = 0;
    }
  };

  const handleReset = () => {
    setIsActive(false);
    if (timerMode === 'focus') {
      setTimeLeft(selectedDuration * 60);
    } else if (timerMode === 'shortBreak') {
      setTimeLeft(selectedShortBreakDuration * 60);
    } else if (timerMode === 'longBreak') {
      setTimeLeft(selectedLongBreakDuration * 60);
    }
    if (ambientSound) {
      ambientSound.pause();
      ambientSound.currentTime = 0;
    }
  };

  const addDistraction = () => {
    const distraction = prompt('What distracted you?');
    if (distraction) {
      setDistractions([...distractions, distraction]);
    }
  };

  const saveSession = () => {
    const session: Session = {
      duration: selectedDuration,
      goal,
      mood,
      notes: sessionNotes,
      tags: sessionTags,
    };
    // TODO: Save session to database
    setShowReflection(false);
    setSessionNotes('');
    setSessionTags([]);
    setDistractions([]);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Focus & Learn</h1>
      
      {/* Pre-session Setup */}
      {!isActive && !showReflection && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6 space-y-6">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Session Setup</h2>
            
            {/* Timer Mode Selection */}
            <div className="flex gap-4 mb-4">
              <button
                onClick={() => setTimerMode('focus')}
                className={`px-4 py-2 rounded-lg ${
                  timerMode === 'focus'
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Focus
              </button>
              <button
                onClick={() => setTimerMode('shortBreak')}
                className={`px-4 py-2 rounded-lg ${
                  timerMode === 'shortBreak'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Short Break
              </button>
              <button
                onClick={() => setTimerMode('longBreak')}
                className={`px-4 py-2 rounded-lg ${
                  timerMode === 'longBreak'
                    ? 'bg-purple-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                }`}
              >
                Long Break
              </button>
            </div>
            
            {/* Duration Selection */}
            <div className="flex gap-4">
              {(timerMode === 'focus' ? focusDurations : 
                timerMode === 'shortBreak' ? shortBreakDurations : 
                longBreakDurations).map((duration) => (
                <button
                  key={duration}
                  onClick={() => {
                    if (timerMode === 'focus') {
                      setSelectedDuration(duration);
                    } else if (timerMode === 'shortBreak') {
                      setSelectedShortBreakDuration(duration);
                    } else {
                      setSelectedLongBreakDuration(duration);
                    }
                  }}
                  className={`px-4 py-2 rounded-lg ${
                    (timerMode === 'focus' && selectedDuration === duration) ||
                    (timerMode === 'shortBreak' && selectedShortBreakDuration === duration) ||
                    (timerMode === 'longBreak' && selectedLongBreakDuration === duration)
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {duration} min
                </button>
              ))}
            </div>

            {/* Goal Input (only show for focus mode) */}
            {timerMode === 'focus' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Learning Goal
                </label>
                <input
                  type="text"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                  placeholder="What do you want to learn?"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            )}

            {/* Mood Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                How are you feeling?
              </label>
              <div className="flex gap-4">
                {moods.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => setMood(emoji)}
                    className={`text-2xl p-2 rounded-lg ${
                      mood === emoji
                        ? 'bg-blue-100 dark:bg-blue-900'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            {/* Ambient Sound Controls */}
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Rain Ambient Sound
              </label>
              <button
                onClick={() => setIsAmbientSoundOn(!isAmbientSoundOn)}
                className={`w-12 h-6 rounded-full ${
                  isAmbientSoundOn ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                } relative`}
              >
                <span
                  className={`absolute w-4 h-4 bg-white rounded-full top-1 transition-transform ${
                    isAmbientSoundOn ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>

            <button
              onClick={handleStart}
              className="w-full py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Start {timerMode === 'focus' ? 'Focus' : timerMode === 'shortBreak' ? 'Short' : 'Long'} Session
            </button>
          </div>
        </div>
      )}

      {/* Active Session */}
      {isActive && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6 space-y-6">
          <div className="text-center">
            <div className="text-6xl font-bold text-gray-900 dark:text-white mb-4">
              {formatTime(timeLeft)}
            </div>
            <div className="text-lg text-gray-600 dark:text-gray-300 mb-4">
              {timerMode === 'focus' ? goal : `${timerMode === 'shortBreak' ? 'Short' : 'Long'} Break`}
            </div>
            <div className="flex justify-center gap-4">
              <button
                onClick={handlePause}
                className="px-6 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600"
              >
                Pause
              </button>
              <button
                onClick={handleReset}
                className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Reset
              </button>
              {timerMode === 'focus' && (
                <button
                  onClick={addDistraction}
                  className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                >
                  Log Distraction
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reflection Modal */}
      {showReflection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-lg w-full space-y-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Session Reflection</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                How was your focus?
              </label>
              <div className="flex gap-4">
                {moods.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => setMood(emoji)}
                    className={`text-2xl p-2 rounded-lg ${
                      mood === emoji
                        ? 'bg-blue-100 dark:bg-blue-900'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Session Notes
              </label>
              <textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                rows={4}
                placeholder="What did you learn? Any insights?"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Tags
              </label>
              <input
                type="text"
                value={sessionTags.join(', ')}
                onChange={(e) => setSessionTags(e.target.value.split(',').map(tag => tag.trim()))}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                placeholder="Add tags (comma-separated)"
              />
            </div>

            {distractions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Distractions Logged:
                </h3>
                <ul className="list-disc list-inside text-gray-600 dark:text-gray-300">
                  {distractions.map((distraction, index) => (
                    <li key={index}>{distraction}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowReflection(false)}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                Skip
              </button>
              <button
                onClick={saveSession}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Save Session
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 